import { z } from 'zod';
import { defineEndpoint } from './contract.ts';
import { CustomIdButton, Snowflake } from './discord/base.ts';
import { defineEvent } from './events.ts';
import { PageQuery, paginated } from './pagination.ts';

// Notifications (design §6.7, plan 1C Chunk 5). Kinds are declared in manifests,
// `<module>.<name>`. A module stages one with `tx.notify(request)`; core's
// `core:notifications` subscriber works out who gets it, stores an inbox record per
// recipient, pushes it live, and sends a Discord DM when their preferences allow.

/** A kind's name: `<module>.<name>` in lower case, with more dotted parts if wanted (`core.user.access_lost`). */
export const NOTIFICATION_KIND = /^[a-z][a-z0-9]*(\.[a-z][a-z0-9_]*)+$/;

export const NotificationChannel = z.enum(['in_app', 'discord']);
export type NotificationChannel = z.infer<typeof NotificationChannel>;

const RecordId = z.string().regex(/^[a-z0-9]{15}$/);

/** One part of an audience: users by id, holders of a permission, a group's members, or a Discord role's holders. */
export const AudienceEntry = z.union([
  z.object({ users: z.array(RecordId).max(1000) }).strict(),
  z.object({ permission: z.string().min(1).max(128) }).strict(),
  z.object({ group: RecordId }).strict(),
  z.object({ role: Snowflake }).strict(),
]);
export type AudienceEntry = z.infer<typeof AudienceEntry>;

/** Who a notification is for: one entry or a list. Resolved when delivered, against active users with access. */
export const Audience = z.union([AudienceEntry, z.array(AudienceEntry).max(20)]);
export type Audience = z.infer<typeof Audience>;

/**
 * Buttons on a notification's Discord DM: at most 5 rows of 5, as in any message, but only buttons that send the press back
 * to ITC Web (no links; the notification's own `link` is the way to a page). Each `customId` must be the notifying module's own,
 * `<module>:<action>:<id>`, which `tx.notify` checks.
 */
export const NotificationButtons = z.array(z.array(CustomIdButton).min(1).max(5)).max(5);
export type NotificationButtons = z.infer<typeof NotificationButtons>;

/** The largest `data`, as JSON. */
export const MAX_NOTIFICATION_DATA_BYTES = 16 * 1024;

export const NotificationRequest = z.object({
  to: Audience,
  kind: z.string().regex(NOTIFICATION_KIND),
  title: z.string().trim().min(1).max(200),
  body: z.string().max(2000).default(''),
  /** A path in ITC Web, such as `/bank/entries/abc`. */
  link: z
    .string()
    .max(512)
    .regex(/^\/(?!\/)[^\s\\]*$/, 'a path starting with a single /')
    .optional(),
  /** Anything the kind's renderer needs. JSON, at most 16 KB. */
  data: z
    .record(z.string(), z.unknown())
    .refine((value) => JSON.stringify(value).length <= MAX_NOTIFICATION_DATA_BYTES, 'at most 16 KB as JSON')
    .optional(),
  /** On the Discord DM only. The in-app record doesn't keep them: it links to the page, where the same actions are. */
  buttons: NotificationButtons.optional(),
  /** The same key for the same recipient within 24 hours is dropped. */
  dedupeKey: z.string().min(1).max(128).optional(),
});
export type NotificationRequest = z.output<typeof NotificationRequest>;
export type NotificationRequestInput = z.input<typeof NotificationRequest>;

/** Staged by `tx.notify`, in the caller's transaction; core's subscriber delivers it. */
export const NotificationRequested = defineEvent({
  type: 'core.notification.requested',
  data: z.object({ module: z.string(), actor: z.string(), request: NotificationRequest }),
});

export const DiscordDelivery = z.enum(['none', 'pending', 'sent', 'failed']);
export type DiscordDelivery = z.infer<typeof DiscordDelivery>;

export const NotificationItem = z.object({
  id: z.string(),
  kind: z.string(),
  module: z.string(),
  title: z.string(),
  body: z.string(),
  link: z.string().nullable(),
  data: z.record(z.string(), z.unknown()),
  readAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  /** Over the kind's hourly cap: kept in the inbox, but not toasted or sent to Discord. */
  suppressed: z.boolean(),
  discord: z.object({ status: DiscordDelivery, error: z.string() }),
});
export type NotificationItem = z.infer<typeof NotificationItem>;

/** A user's delivery choices for one kind, with the kind's description and defaults filled in. */
export const NotificationPreference = z.object({
  kind: z.string(),
  module: z.string(),
  /** The module's title ("ITC Web" for core's own kinds), for grouping on the settings page. */
  moduleTitle: z.string(),
  description: z.string(),
  /** In-app delivery can't be turned off. */
  critical: z.boolean(),
  inApp: z.boolean(),
  discord: z.boolean(),
  /** 0 until the user first changes it. */
  version: z.number().int(),
});
export type NotificationPreference = z.infer<typeof NotificationPreference>;

const Unread = z.object({ unreadCount: z.number().int() });

export const listNotifications = defineEndpoint({
  method: 'GET',
  path: '/notifications',
  permission: 'authenticated',
  query: PageQuery.extend({ unread: z.enum(['true', 'false']).optional() }),
  response: paginated(NotificationItem).extend(Unread.shape),
});

export const markNotificationRead = defineEndpoint({
  method: 'POST',
  path: '/notifications/:id/read',
  permission: 'authenticated',
  params: z.object({ id: RecordId }),
  response: z.object({ item: NotificationItem }).extend(Unread.shape),
});

export const markAllNotificationsRead = defineEndpoint({
  method: 'POST',
  path: '/notifications/read-all',
  permission: 'authenticated',
  response: z.object({ updated: z.number().int() }).extend(Unread.shape),
});

export const getNotificationPrefs = defineEndpoint({
  method: 'GET',
  path: '/notifications/prefs',
  permission: 'authenticated',
  response: z.object({ items: z.array(NotificationPreference) }),
});

export const updateNotificationPref = defineEndpoint({
  method: 'PUT',
  path: '/notifications/prefs',
  permission: 'authenticated',
  body: z.object({ kind: z.string().regex(NOTIFICATION_KIND), inApp: z.boolean(), discord: z.boolean(), version: z.number().int().min(0) }),
  response: z.object({ item: NotificationPreference }),
});
